/**
 * Incident Analytics Dashboard
 * Phase 2.1: Manual vs system-generated trends, risk/protocol correlation
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BarChart3, AlertTriangle, Activity, PieChart, Target, FileText } from 'lucide-react';
import './IncidentAnalyticsDashboard.css';

const IncidentAnalyticsDashboard = ({ incidents = [], risks = [], protocols = [], onBack }) => {
  const { t } = useTranslation('incidents');

  const manualCount = incidents.filter(i => (i.origin || 'manual') === 'manual').length;
  const systemCount = incidents.filter(i => i.origin === 'system_generated').length;

  const bySeverity = {
    Critical: incidents.filter(i => i.severity === 'Critical').length,
    High: incidents.filter(i => i.severity === 'High').length,
    Medium: incidents.filter(i => i.severity === 'Medium').length,
    Low: incidents.filter(i => i.severity === 'Low').length
  };

  const byType = incidents.reduce((acc, inc) => {
    const k = inc.type || 'Other';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const byRisk = incidents.reduce((acc, inc) => {
    if (!inc.riskId) return acc;
    acc[inc.riskId] = (acc[inc.riskId] || 0) + 1;
    return acc;
  }, {});
  const topRisks = Object.entries(byRisk)
    .map(([riskId, count]) => ({
      riskId,
      riskName: risks.find(r => r.id === riskId)?.riskScenario || riskId,
      count
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  const byProtocol = incidents.reduce((acc, inc) => {
    if (!inc.protocolId) return acc;
    acc[inc.protocolId] = (acc[inc.protocolId] || 0) + 1;
    return acc;
  }, {});
  const topProtocols = Object.entries(byProtocol)
    .map(([protocolId, count]) => ({
      protocolId,
      protocolName: protocols.find(p => (p.firestoreId || p.id) === protocolId)?.name || protocolId,
      count
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  return (
    <div className="incident-analytics-dashboard">
      <div className="incident-analytics-header">
        <div>
          <button type="button" className="btn-back" onClick={onBack}>
            <ArrowLeft size={18} />
            {t('backToList')}
          </button>
          <h1>{t('incidentAnalyticsTitle')}</h1>
          <p>{t('incidentAnalyticsSubtitle')}</p>
        </div>
      </div>

      <div className="incident-analytics-stats">
        <div className="stat-card total">
          <div className="stat-icon"><Activity size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{incidents.length}</div>
            <div className="stat-label">{t('totalIncidents')}</div>
          </div>
        </div>
        <div className="stat-card manual">
          <div className="stat-icon"><FileText size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{manualCount}</div>
            <div className="stat-label">{t('originManual')}</div>
          </div>
        </div>
        <div className="stat-card system">
          <div className="stat-icon"><AlertTriangle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{systemCount}</div>
            <div className="stat-label">{t('originSystem')}</div>
          </div>
        </div>
        <div className="stat-card critical">
          <div className="stat-icon"><Activity size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{bySeverity.Critical + bySeverity.High}</div>
            <div className="stat-label">{t('criticalHighCount')}</div>
          </div>
        </div>
      </div>

      <div className="incident-analytics-sections">
        <div className="analytics-section">
          <h2><PieChart size={20} /> {t('originDistribution')}</h2>
          <div className="distribution-bars">
            <div className="dist-bar">
              <span className="dist-label">{t('originManual')}</span>
              <div className="dist-track">
                <div className="dist-fill manual" style={{ width: incidents.length ? `${(manualCount / incidents.length) * 100}%` : 0 }} />
              </div>
              <span className="dist-count">{manualCount}</span>
            </div>
            <div className="dist-bar">
              <span className="dist-label">{t('originSystem')}</span>
              <div className="dist-track">
                <div className="dist-fill system" style={{ width: incidents.length ? `${(systemCount / incidents.length) * 100}%` : 0 }} />
              </div>
              <span className="dist-count">{systemCount}</span>
            </div>
          </div>
        </div>

        <div className="analytics-section">
          <h2><BarChart3 size={20} /> {t('bySeverity')}</h2>
          <div className="distribution-bars">
            {['Critical', 'High', 'Medium', 'Low'].map((sev) => (
              <div key={sev} className="dist-bar">
                <span className="dist-label">{t(`severity${sev}`)}</span>
                <div className="dist-track">
                  <div className={`dist-fill severity-${sev.toLowerCase()}`} style={{ width: incidents.length ? `${(bySeverity[sev] || 0) / incidents.length * 100}%` : 0 }} />
                </div>
                <span className="dist-count">{bySeverity[sev] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="analytics-section">
          <h2><Target size={20} /> {t('topRisksByIncidents')}</h2>
          {topRisks.length === 0 ? (
            <p className="empty-message">{t('noData')}</p>
          ) : (
            <ul className="top-list">
              {topRisks.map(r => (
                <li key={r.riskId}>
                  <span className="item-name">{r.riskName}</span>
                  <span className="item-count">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="analytics-section">
          <h2><BarChart3 size={20} /> {t('topProtocolsByIncidents')}</h2>
          {topProtocols.length === 0 ? (
            <p className="empty-message">{t('noData')}</p>
          ) : (
            <ul className="top-list">
              {topProtocols.map(p => (
                <li key={p.protocolId}>
                  <span className="item-name">{p.protocolName}</span>
                  <span className="item-count">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncidentAnalyticsDashboard;
